import React from 'react';
import { ButtonCommon } from 'components/common';
import { Info, CalendarBlank, ArrowRight } from '@phosphor-icons/react';
import { tv, type VariantProps } from 'tailwind-variants';
import { formatDate } from 'utils';
import { PurchaseOrderStatusCode, DateDisplayProps, PurchaseOrderCardProps } from 'types';
import { getStatusDisplay, isDateBeforeToday, mapStatusForCardStyles } from 'utils/purchaseOrderUtils';

// Define all card variants using tv instead of cva
export const cardStyles = tv({
  base: 'bg-white rounded-lg overflow-hidden',
  variants: {
    status: {
      RECEIVED: '',
      READY: '',
      INPROGRESS: '',
      DELAYED: '',
      CANCELLED: '',
    },
    isCompact: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    status: 'READY',
    isCompact: false,
  },
});

// Status bar styling
const statusBar = tv({
  base: 'rounded',
  variants: {
    status: {
      READY: 'bg-[var(--primaryColor500)]',
      INPROGRESS: 'bg-[var(--tealColor700)]',
      RECEIVED: 'bg-[var(--sematicSuccessColor500)]',
      DELAYED: 'bg-[var(--sematicErrorColor500)]',
      CANCELLED: 'bg-[var(--textBlack56)]',
    },
    isCompact: {
      true: 'w-2 h-[64px] self-stretch',
      false: 'w-2 h-[64px]',
    },
  },
  defaultVariants: {
    status: 'READY',
    isCompact: false,
  },
});

// Status indicator dot styling
const statusDot = tv({
  base: 'w-1.5 h-1.5 rounded-full',
  variants: {
    status: {
      READY: 'bg-[var(--primaryColor500)]',
      INPROGRESS: 'bg-[var(--tealColor700)]',
      RECEIVED: 'bg-[var(--sematicSuccessColor500)]',
      DELAYED: 'bg-[var(--sematicErrorColor500)]',
      CANCELLED: 'bg-[var(--textBlack56)]',
    },
  },
  defaultVariants: {
    status: 'READY',
  },
});

// Status text styling
const statusText = tv({
  base: 'text-sm font-bold',
  variants: {
    status: {
      READY: 'text-[var(--primaryColor500)]',
      INPROGRESS: 'text-[var(--tealColor700)]',
      RECEIVED: 'text-[var(--sematicSuccessColor500)]',
      DELAYED: 'text-[var(--sematicErrorColor500)]',
      CANCELLED: 'text-[var(--textBlack56)]',
    },
  },
  defaultVariants: {
    status: 'READY',
  },
});

// Date display styling
const dateDisplay = tv({
  base: 'h-8 px-4 py-2.5 rounded-full flex items-center gap-2',
  variants: {
    variant: {
      default: 'bg-[var(--bgBlackOpacity)]',
      error: 'bg-[var(--bgRed50)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date icon styling
const dateIcon = tv({
  base: 'text-xl',
  variants: {
    variant: {
      default: 'text-[var(--textBlack100)]',
      error: 'text-[var(--sematicErrorColor500)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date text styling
const dateText = tv({
  base: 'text-center text-sm leading-5',
  variants: {
    variant: {
      default: 'text-[var(--textBlack100)]',
      error: 'text-[var(--sematicErrorColor500)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date display component with calendar icon

const DateDisplay: React.FC<DateDisplayProps> = ({ label, date, variant = 'default' }) => {
  return (
    <div className='h-8 flex items-center gap-2'>
      <div className='text-[14px] leading-5 text-[rgba(0,0,0,0.56)]'>{label}</div>
      <div className={dateDisplay({ variant })}>
        <CalendarBlank size={20} className={dateIcon({ variant })} />
        <div className={dateText({ variant })}>{formatDate(date)}</div>
      </div>
    </div>
  );
};

export const PurchaseOrderCard: React.FC<PurchaseOrderCardProps> = ({
  code,
  createdAt,
  deliveryDate,
  warehouseName,
  supplierName,
  status,
  isCompact = false,
  delayDays,
  onDetail,
  onImport,
  onView,
  onCancel,
}) => {
  const isDelayed =
    (status === PurchaseOrderStatusCode.READY || status === PurchaseOrderStatusCode.INPROGRESS) &&
    isDateBeforeToday(deliveryDate);
  const isImportable = status === PurchaseOrderStatusCode.READY && !isDelayed;
  const isInProgress = status === PurchaseOrderStatusCode.INPROGRESS && !isDelayed;
  const showViewButton =
    status === PurchaseOrderStatusCode.RECEIVED || status === PurchaseOrderStatusCode.CANCELLED || isDelayed;
  const mappedStatus = mapStatusForCardStyles(status, deliveryDate);
  const calculateDelayDays = () => {
    if (!isDelayed) return delayDays;
    const today = new Date();
    const delivery = new Date(deliveryDate);
    const diffTime = Math.abs(today.getTime() - delivery.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const displayDelayDays = calculateDelayDays();

  return (
    <div className={cardStyles({ status: mappedStatus, isCompact })}>
      <div className='px-6 py-3 flex flex-col gap-3'>
        <div className='flex justify-between items-start border-b py-3'>
          <div className='flex items-center gap-4'>
            <div className={statusBar({ status: mappedStatus, isCompact: true })}></div>

            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <span className='text-base font-bold text-[var(--textBlack100)]'>{code}</span>
                <span className='text-base font-bold text-[var(--textBlack100)]'>-</span>
                <span className='text-base font-bold text-[var(--textBlack100)]'>{supplierName}</span>

                <div className='flex items-center gap-2'>
                  <div
                    className='w-3 h-3 p-[3px] rounded-full flex items-center justify-center'
                    style={{
                      background: `radial-gradient(ellipse 50% 50% at 50% 50%, ${
                        isDelayed
                          ? 'var(--sematicErrorColor500)'
                          : status === PurchaseOrderStatusCode.READY
                            ? 'var(--primaryColor500)'
                            : status === PurchaseOrderStatusCode.INPROGRESS
                              ? 'var(--tealColor700) '
                              : status === PurchaseOrderStatusCode.RECEIVED
                                ? 'var(--sematicSuccessColor500)'
                                : 'var(--textBlack56)'
                      } 0%, rgba(59, 130, 246, 0) 100%)`,
                    }}
                  >
                    <div className={statusDot({ status: mappedStatus })}></div>
                  </div>
                  <span className={statusText({ status: mappedStatus })}>
                    {getStatusDisplay(status, deliveryDate)}
                    {isDelayed && displayDelayDays && (
                      <span className='text-[var(--sematicErrorColor500)] font-normal ml-1'>
                        ({displayDelayDays} ngày)
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* Warehouse name */}
              <div className='h-8 px-4 py-1.5 bg-[var(--bgBlackOpacity)] rounded-full w-max'>
                <span className='text-sm text-[var(--textBlack100)]'>{warehouseName}</span>
              </div>
            </div>
          </div>

          {/* Chi tiết button */}
          <div className='flex items-center'>
            <ButtonCommon
              color='none'
              size='md'
              radius='rounded'
              className='hover:font-normal'
              icon={<Info size={16} />}
              iconPosition='right'
              onClick={onDetail}
            >
              Chi tiết
            </ButtonCommon>
          </div>
        </div>

        {/* Bottom section with dates and action buttons */}
        <div className='flex justify-between items-center'>
          <div className='flex  flex-wrap items-center gap-6 gap-y-3'>
            <DateDisplay label='Ngày tạo đơn' date={createdAt ? new Date(createdAt) : new Date()} />

            {status === PurchaseOrderStatusCode.RECEIVED ? (
              <DateDisplay label='Ngày nhập hàng' date={deliveryDate} />
            ) : (
              <DateDisplay
                label='Ngày dự tính nhập hàng'
                date={deliveryDate}
                variant={isDelayed ? 'error' : 'default'}
              />
            )}
          </div>

          {/* Action buttons */}
          <div>
            {isImportable && (
              <ButtonCommon color='primary' size='md' radius='rounded' className='w-[220px]' onClick={onImport}>
                Nhập hàng
              </ButtonCommon>
            )}
            {isInProgress && (
              <ButtonCommon
                color='primary'
                size='md'
                icon={<ArrowRight size={16} />}
                iconPosition='right'
                radius='rounded'
                className='w-[220px]'
                onClick={onImport}
              >
                Tiếp tục nhập hàng
              </ButtonCommon>
            )}
            {showViewButton && (
              <div className='flex gap-2'>
                {status === PurchaseOrderStatusCode.RECEIVED && onCancel && (
                  <ButtonCommon
                    color='outline'
                    size='md'
                    radius='rounded'
                    className='w-[220px] !border-[var(--sematicErrorColor500)] !text-[var(--sematicErrorColor500)] hover:!bg-[var(--sematicErrorColor50)]'
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancel();
                    }}
                  >
                    Trả hàng
                  </ButtonCommon>
                )}
                <ButtonCommon
                  color='outline'
                  size='md'
                  radius='rounded'
                  className='w-[220px]'
                  icon={<ArrowRight size={16} />}
                  iconPosition='right'
                  onClick={onView}
                >
                  Xem ngay
                </ButtonCommon>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export type CardStylesVariantProps = VariantProps<typeof cardStyles>;
export default PurchaseOrderCard;
